import React, {useCallback, useMemo} from 'react'
import {useLocation, useNavigate} from 'react-router-dom'
import {HelpCircle} from 'lucide-react'
import styled from 'styled-components'
import type {FaqModule} from '~/src/models/faq'
import {Button} from '~/src/design-system/Primitives'
import {useSettingsRowPermission} from '~/src/components/platform/settings/settingsPermission'

const PATHNAME_TO_FAQ_MODULE: Record<string, FaqModule> = {
  companies: 'manage-companies',
  company: 'manage-companies',
  agencies: 'manage-agencies',
  agency: 'manage-agencies',
  users: 'users',
  candidates: 'candidates',
  applicants: 'candidates',
  global: 'global-settings',
  payroll: 'payroll',
  dashboard: 'dashboard',
  orders: 'manage-orders',
  profile: 'manage-profile',
  positions: 'positions',
  invoices: 'invoices',
  timesheets: 'timesheets',
}

const HelpButtonWrapper = styled.div`
  position: relative;
  display: inline-flex;
`

const Tooltip = styled.span`
  position: absolute;
  bottom: -2.25rem;
  left: 50%;
  transform: translateX(-50%);
  padding: var(--space-1) var(--space-2);
  background: #1f2937;
  color: var(--white);
  border-radius: var(--radius-sm);
  font-size: 0.6875rem;
  white-space: nowrap;
  pointer-events: none;
  opacity: 0;
  transition: opacity 0.15s ease;

  ${HelpButtonWrapper}:hover & {
    opacity: 1;
  }
`

const HelpIconButton: React.FC = () => {
  const location = useLocation()
  const navigate = useNavigate()
  // The button opens Settings > Help / FAQ, so it shows on that page's own row, decided as the nav
  // and the route decide it (STFFLG-4185): an absent row, or no tree at all, still shows it.
  const {canView: canViewHelp} = useSettingsRowPermission('help_faq')

  const currentModule = useMemo((): FaqModule | null => {
    const segments = location.pathname.split('/').filter(Boolean)
    for (const segment of segments) {
      const lower = segment.toLowerCase()
      if (PATHNAME_TO_FAQ_MODULE[lower]) {
        return PATHNAME_TO_FAQ_MODULE[lower]
      }
    }
    return null
  }, [location.pathname])

  const handleClick = useCallback(() => {
    const moduleParam = currentModule ? `?module=${currentModule}` : ''
    navigate(`/settings/help${moduleParam}`)
  }, [navigate, currentModule])

  if (!canViewHelp) return null

  return (
    <HelpButtonWrapper>
      <Button
        variant="ghost"
        onClick={handleClick}
        aria-label="Help and FAQ"
        data-testid="help-icon-button">
        <HelpCircle size={20} />
      </Button>
      <Tooltip>Need help? View FAQs</Tooltip>
    </HelpButtonWrapper>
  )
}

export default HelpIconButton
